import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/Seo";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "coach" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "string" ? v : v.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(form);
      toast.success("Cuenta creada");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen emerald-gradient flex items-center justify-center p-4">
      <Seo
        title="Crear cuenta · EF Concepción — Gestión Deportiva"
        description="Regístrate como entrenador o director deportivo del club EF Concepción y gestiona plantillas, entrenamientos, convocatorias, minutos y evaluaciones de tus jugadores."
        path="/register"
      />
      <div className="w-full max-w-5xl grid md:grid-cols-2 gap-6 items-center">
        <div className="hidden md:block">
          <div className="inline-flex items-center gap-3 mb-6">
            <img
              src="/logo.jpg"
              alt="EF Concepción"
              className="w-14 h-14 rounded-xl object-contain bg-white border border-slate-200 shadow-sm"
            />
            <div>
              <div className="font-black text-2xl leading-tight" style={{ fontFamily: "Nunito" }}>
                EF Concepción
              </div>
              <div className="text-xs uppercase tracking-widest text-slate-500">
                Madrid · Gestión
              </div>
            </div>
          </div>
          <h1
            className="text-4xl sm:text-5xl font-black leading-tight mb-4"
            style={{ fontFamily: "Nunito" }}
          >
            Crea tu cuenta
            <br />
            <span className="text-purple-700">y empieza a gestionar tu equipo.</span>
          </h1>
          <p className="text-slate-600 text-lg leading-relaxed max-w-md">
            Regístrate como entrenador o director deportivo del club EF Concepción. Podrás
            gestionar plantillas, planificar entrenamientos, controlar convocatorias y minutos,
            valorar el rendimiento físico y emitir informes en PDF para cada jugador. Todo
            centralizado en un panel gratuito, en español y adaptado al fútbol base y amateur.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-slate-600 max-w-md">
            <li>
              <strong className="text-slate-800">Panel del entrenador:</strong> asistencia,
              convocatorias, minutos jugados y evaluaciones individuales por jugador.
            </li>
            <li>
              <strong className="text-slate-800">Panel del director deportivo:</strong> visión
              global del club, comparativas entre equipos y evaluación de entrenadores.
            </li>
            <li>
              <strong className="text-slate-800">Informes PDF y Excel</strong> personalizados de
              jugadores, equipos, partidos y sesiones de entrenamiento.
            </li>
          </ul>
        </div>

        <Card className="w-full max-w-md border-slate-200 shadow-sm md:justify-self-end">
          <CardHeader>
            <CardTitle
              className="text-2xl font-black md:hidden"
              style={{ fontFamily: "Nunito" }}
              tabIndex={-1}
            >
              Crear cuenta
            </CardTitle>
            <p className="text-sm text-slate-500">Únete a la plataforma del club</p>
          </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div>
              <Label htmlFor="name">Nombre completo</Label>
              <Input id="name" value={form.name} onChange={set("name")} required data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email">Correo electrónico</Label>
              <Input id="email" type="email" value={form.email} onChange={set("email")} required data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Contraseña (mín. 6)</Label>
              <Input id="password" type="password" value={form.password} onChange={set("password")} required minLength={6} data-testid="register-password-input" />
            </div>
            <div>
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={set("role")}>
                <SelectTrigger data-testid="register-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="coach">Entrenador</SelectItem>
                  <SelectItem value="admin">Director deportivo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
            <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold" disabled={loading} data-testid="register-submit-button">
              {loading ? "Creando..." : "Crear cuenta"}
            </Button>
            <div className="text-sm text-center text-slate-500">
              ¿Ya tienes cuenta?{" "}
              <Link to="/login" className="text-purple-700 font-semibold" data-testid="link-login">Inicia sesión</Link>
            </div>
          </form>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
